import { Link, useParams } from 'react-router'
import { motion } from 'motion/react'
import { ArrowLeft } from 'lucide-react'
import { useArticle, useNewsList, useSettings } from '@/lib/queries'
import { fileUrl } from '@/lib/pb'
import { formatDate, plainText, titleCase } from '@/lib/utils'
import { Container } from '@/components/site/primitives'
import { Button } from '@/components/ui/button'
import { ErrorState, LoadingBlock } from '@/components/ui/feedback'
import { MatchCard, NewsCard } from '@/components/site/cards'
import { usePageMeta } from '@/components/layout/site-layout'

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>()
  const { data: article, isLoading, isError } = useArticle(slug)
  const { data: all = [] } = useNewsList()
  const { data: settings } = useSettings()

  usePageMeta(article?.title, article ? article.excerpt || plainText(article.body, 200) : undefined)

  if (isLoading)
    return (
      <LoadingBlock
        className="min-h-[70vh] pt-[calc(var(--header-h)+4rem)]"
        label="Loading article"
      />
    )

  if (isError || !article) {
    return (
      <Container className="pt-[calc(var(--header-h)+5rem)] pb-24">
        <ErrorState
          title="Article not found"
          description="It may have been unpublished or moved."
          action={
            <Button asChild variant="outline">
              <Link to="/news">All news</Link>
            </Button>
          }
        />
      </Container>
    )
  }

  const cover = fileUrl(article, article.cover, '1600x0')
  const related = all.filter((other) => other.id !== article.id).slice(0, 3)
  const relatedMatch = article.expand?.related_match

  return (
    <>
      <article>
        <header className="border-hairline bg-ink relative overflow-hidden border-b pt-[calc(var(--header-h)+2rem)] pb-12 md:pt-[calc(var(--header-h)+3rem)] md:pb-16">
          {cover ? (
            <>
              <img
                src={cover}
                alt=""
                className="absolute inset-0 size-full object-cover opacity-20"
              />
              <div className="from-ink via-ink/80 to-ink/60 absolute inset-0 bg-gradient-to-t" />
            </>
          ) : null}

          <Container size="narrow" className="relative">
            <Button asChild variant="ghost" size="sm" className="mb-8 -ml-3">
              <Link to="/news">
                <ArrowLeft className="size-4" aria-hidden />
                News
              </Link>
            </Button>

            <motion.div
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
            >
              {article.category ? (
                <span className="condensed bg-blood text-bone px-2.5 py-1 text-[0.625rem] font-bold tracking-[0.18em]">
                  {titleCase(article.category)}
                </span>
              ) : null}

              <h1 className="condensed text-balance-safe text-bone mt-5 text-[clamp(2rem,5.5vw,3.75rem)] leading-[1.05] font-bold">
                {article.title}
              </h1>

              <div className="text-bone-faint mt-6 flex flex-wrap items-center gap-x-4 gap-y-2 text-sm">
                <time dateTime={article.published_at}>{formatDate(article.published_at)}</time>
                {article.author ? (
                  <>
                    <span aria-hidden>·</span>
                    <span>{article.author}</span>
                  </>
                ) : null}
              </div>

              {article.excerpt ? (
                <p className="border-blood text-bone-dim mt-7 border-l-2 pl-5 text-lg leading-relaxed">
                  {article.excerpt}
                </p>
              ) : null}
            </motion.div>
          </Container>
        </header>

        {cover ? (
          <Container size="narrow" className="-mt-2">
            <img
              src={cover}
              alt=""
              className="aspect-16/9 w-full object-cover"
              loading="eager"
              fetchPriority="high"
            />
          </Container>
        ) : null}

        <Container size="narrow" className="py-12 md:py-16">
          {article.body ? (
            <div
              className="prose-viking text-[1.0625rem]"
              dangerouslySetInnerHTML={{ __html: article.body }}
            />
          ) : null}

          {relatedMatch ? (
            <div className="mt-12">
              <p className="eyebrow text-blood mb-4">The match</p>
              <MatchCard
                match={relatedMatch}
                clubName={settings?.club_name ?? 'AFC Vikings Cairo'}
              />
            </div>
          ) : null}
        </Container>
      </article>

      {related.length > 0 ? (
        <section className="border-hairline bg-ink-sunken border-t py-14 md:py-20">
          <Container>
            <h2 className="condensed text-bone mb-8 text-xl font-bold tracking-[0.1em]">
              More from the club
            </h2>
            <div className="grid gap-4 md:grid-cols-3">
              {related.map((other, index) => (
                <NewsCard key={other.id} article={other} index={index} />
              ))}
            </div>
          </Container>
        </section>
      ) : null}
    </>
  )
}
